import { useGoogleMap } from '@react-google-maps/api';
import isEquil from 'lodash/isEqual';
import { useTranslation } from 'next-i18next';
import React, {
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { mapCoordinatesToLatLng } from '@hotelplan/components.common.map-pin';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { BsLink } from '@hotelplan/core.basis.bs-link';
import { FdrCoordinates, FdrImage, FdrLink } from '@hotelplan/supergraph-api';
import { FdrMapCardInfo, FdrMapInfoBlock } from 'components/domain/fdr-map';
import { FdrHotelMapItemFragment } from 'schemas/query/hotel/fdr-nearest-hotels.generated';
import {
  ACTIVE_PIN_ICON,
  INACTIVE_PIN_ICON,
  PRODUCT_GALLERY_MAP_ZOOM,
} from './fdr-hotel-map.constants';
import { FdrMapPinWrapper } from './fdr-hotel-map.styles';

interface IFdrNearestHotelsMapPinsProps {
  nearestHotels: Array<FdrHotelMapItemFragment>;
  activeFdrHotelCoords: FdrCoordinates;
}

export function FdrNearestHotelsMapPins({
  nearestHotels,
  activeFdrHotelCoords,
}: IFdrNearestHotelsMapPinsProps): ReactElement {
  const map = useGoogleMap();
  const [active, setActive] = useState<number>(undefined);

  useEffect(
    function () {
      if (!map || !nearestHotels) return;

      const bounds = new google.maps.LatLngBounds();

      nearestHotels.forEach(hotel => {
        bounds.extend(mapCoordinatesToLatLng(hotel.coordinates));
      });

      requestAnimationFrame(() => {
        map.fitBounds(bounds, 30);
      });
    },
    [map, nearestHotels]
  );

  return (
    <>
      {nearestHotels?.map((nearestHotel, index) => {
        if (!nearestHotel) return null;

        const isActive = isEquil(
          nearestHotel.coordinates,
          activeFdrHotelCoords
        );

        return (
          <FdrHotelMapPin
            key={`${nearestHotel.coordinates}-${index}`}
            coordinates={nearestHotel.coordinates}
            link={nearestHotel.webMeta?.descriptionWebMeta?.link}
            pinIcon={isActive ? ACTIVE_PIN_ICON : INACTIVE_PIN_ICON}
            image={nearestHotel.hero}
            title={nearestHotel.name}
            isActive={active === index}
            onHover={setActive}
            index={index}
          />
        );
      })}
    </>
  );
}

interface IFdrSingleHotelMapPinProps {
  coordinates: FdrCoordinates;
}

export function FdrSingleHotelMapPin({
  coordinates,
}: IFdrSingleHotelMapPinProps): ReactElement {
  const map = useGoogleMap();

  useEffect(() => {
    if (!map || !coordinates) return;

    requestAnimationFrame(() => {
      map.setCenter(mapCoordinatesToLatLng(coordinates));
      map.setZoom(PRODUCT_GALLERY_MAP_ZOOM);
    });
  }, [map, coordinates]);

  if (!coordinates) return null;

  return <FdrHotelMapPin coordinates={coordinates} />;
}

interface IFdrHotelMapPinProps {
  coordinates: FdrCoordinates;
  pinIcon?: string;
  isActive?: boolean;
  onHover?: (index?: number) => void;
  index?: number;
  image?: FdrImage;
  title?: string;
  link?: FdrLink;
}

function FdrHotelMapPin(props: IFdrHotelMapPinProps): ReactElement {
  const {
    coordinates,
    pinIcon = ACTIVE_PIN_ICON,
    isActive,
    onHover,
    index,
    image,
    title,
    link,
  } = props;

  function handleHover() {
    if (pinIcon !== ACTIVE_PIN_ICON) {
      onHover(index);
    }
  }

  function handleCloseClick() {
    onHover(undefined);
  }

  return (
    <FdrMapPinWrapper coordinates={coordinates} object={props}>
      {isActive ? (
        <FdrMapInfoBlock
          latLng={mapCoordinatesToLatLng(coordinates)}
          onCloseClick={handleCloseClick}
        >
          <FdrHotelInfoWindow image={image} title={title} link={link} />
        </FdrMapInfoBlock>
      ) : null}
      <BsIcon name={pinIcon} size="xxlg" onMouseOver={handleHover} />
    </FdrMapPinWrapper>
  );
}

interface IFdrHotelInfoWindowProps {
  title: string;
  image: FdrImage;
  link: FdrLink;
}

function FdrHotelInfoWindow({
  image,
  title,
  link,
}: IFdrHotelInfoWindowProps): ReactElement {
  const [t] = useTranslation('pdp');

  return (
    <FdrLinkBox link={link}>
      <FdrMapCardInfo
        link={link}
        image={image}
        title={title}
        iconName="hotel-icon"
        iconDescription={t('inHotel')}
      />
    </FdrLinkBox>
  );
}

interface IFdrLinkBoxProps {
  link: FdrLink;
}

function FdrLinkBox({
  children,
  link,
}: PropsWithChildren<IFdrLinkBoxProps>): ReactElement {
  if (link && link?.url)
    return (
      <BsLink href={link.url} target="_blank" className="infoWindow-link-box">
        {children}
      </BsLink>
    );

  return <div>{children}</div>;
}
